import { Col } from "antd";
import { Form, Select } from "formik-antd";
import { useTranslation } from "react-i18next";

/**
 * Renders a form item with a select input for choosing a verb filter.
 * @param {Object} props - The component props.
 * @param {(value: string) => void} props.setFilterVerb - Callback function to set the filter verb.
 * @returns {JSX.Element} The rendered form item containing the select input.
 */
function DateVerb() {
  const { t } = useTranslation();

  return (
    <Form.Item name="verb">
      <Select name="verb" size="large">
        <Select.Option value="during">{t("translation:during")}</Select.Option>
        <Select.Option value="before">{t("translation:before")}</Select.Option>
        <Select.Option value="after">{t("translation:after")}</Select.Option>
      </Select>
    </Form.Item>
  );
}

/**
 * Renders a form item with a select input for choosing a string verb.
 *
 * @returns {JSX.Element} A form item containing a select input for a string verb.
 */
function StringVerb() {
  const { t } = useTranslation();

  return (
    <Form.Item name="verb">
      <Select name="verb" size="large">
        <Select.Option value="equals">{t("translation:is")}</Select.Option>
        <Select.Option value="not">{t("translation:not")}</Select.Option>
        <Select.Option value="sw">{t("translation:starts_with")}</Select.Option>
        <Select.Option value="ew">{t("translation:ends_with")}</Select.Option>
        <Select.Option value="inc">{t("translation:includes")}</Select.Option>
        <Select.Option value="exc">{t("translation:excludes")}</Select.Option>
      </Select>
    </Form.Item>
  );
}
/**
 * Renders a form item with a select input for verb selection.
 * @returns {JSX.Element} The rendered form item containing a select dropdown for verb selection.
 */
function UuidVerb() {
  const { t } = useTranslation();

  return (
    <Form.Item name="verb">
      <Select name="verb" size="large">
        <Select.Option value="equals">{t("translation:is")}</Select.Option>
        <Select.Option value="not">{t("translation:not")}</Select.Option>
      </Select>
    </Form.Item>
  );
}

/**
 * Renders a form item containing a select dropdown with number comparison verbs.
 * @returns {JSX.Element} The form item with a select dropdown for number verb options.
 */
function NumberVerb() {
  const { t } = useTranslation();

  return (
    <Form.Item name="verb">
      <Select name="verb" size="large">
        <Select.Option value="equals">{t("translation:is")}</Select.Option>
        <Select.Option value="not">{t("translation:not")}</Select.Option>
        <Select.Option value="gt">
          {t("translation:greater_than")}
        </Select.Option>
        <Select.Option value="lt">{t("translation:less_than")}</Select.Option>
      </Select>
    </Form.Item>
  );
}

/**
 * DefaultVerb renders a form item with a translation-enabled select component for verbs.
 *
 * @returns {JSX.Element} The JSX element representing the form item with the select.
 */
function DefaultVerb() {
  const { t } = useTranslation();
  return (
    <Form.Item name="verb">
      <Select name="verb" size="large">
        <Select.Option value="equals">{t("translation:is")}</Select.Option>
        <Select.Option value="not">{t("translation:not")}</Select.Option>
        <Select.Option value="sw">{t("translation:starts_with")}</Select.Option>
        <Select.Option value="ew">{t("translation:ends_with")}</Select.Option>
        <Select.Option value="inc">{t("translation:includes")}</Select.Option>
        <Select.Option value="exc">{t("translation:excludes")}</Select.Option>
        <Select.Option value="gt">
          {t("translation:greater_than")}
        </Select.Option>
        <Select.Option value="lt">{t("translation:less_than")}</Select.Option>
      </Select>
    </Form.Item>
  );
}

/**
 * A React component that renders different verb components based on the provided type.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.type - The type of the verb, which determines which component to render.
 * @param {function} props.setFilterVerb - A function to set the filter verb value.
 *
 * @returns {JSX.Element} The appropriate verb component wrapped in a Col component.
 */
export default function Verb({ type }: { type: string }) {
  return (
    <Col span={2}>
      {type === "DateTime" && <DateVerb />}
      {type === "Number" && <NumberVerb />}
      {type === "String" && <StringVerb />}
      {type === "UUID" && <UuidVerb />}
      {type === "None" && <DefaultVerb />}
      {type === "" && <DefaultVerb />}
    </Col>
  );
}
